<script setup lang="ts">
import SourceToolHoverCard from '~/components/ui/SourceToolHoverCard.vue'
import iconDropdown from '~/assets/icons/rte/dropdown.svg'
import type { SourceRichTextHeadingValue, SourceRichTextTool } from '~/composables/ui/useSourceRichTextEditor'

defineProps<{
  label: string
  headingValue: SourceRichTextHeadingValue
  canEdit: boolean
  editorReady: boolean
  inlineTools: SourceRichTextTool[]
  blockTools: SourceRichTextTool[]
  embedTools: SourceRichTextTool[]
}>()

const emit = defineEmits<{
  'heading-change': [value: string]
}>()

function maskStyle(url: string): Record<string, string> {
  return {
    WebkitMaskImage: `url("${url}")`,
    maskImage: `url("${url}")`,
  }
}

function onHeadingSelect(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  emit('heading-change', value)
}
</script>

<template>
  <div class="source-rte__toolbar" role="toolbar" :aria-label="`Mise en forme ${label}`">
    <label class="source-rte__select">
      <span class="visually-hidden">Style de paragraphe</span>
      <select
        class="source-rte__dropdown source-rte__dropdown--select"
        :value="headingValue"
        :disabled="!canEdit || !editorReady"
        aria-label="Style de paragraphe"
        @change="onHeadingSelect"
      >
        <option value="paragraph">Normal</option>
        <option value="2">Titre 1</option>
        <option value="3">Titre 2</option>
      </select>
      <span class="source-rte__glyph source-rte__chevron" :style="maskStyle(iconDropdown)" aria-hidden="true" />
    </label>

    <span class="source-rte__group" role="group" aria-label="Inline">
      <SourceToolHoverCard
        v-for="tool in inlineTools"
        :key="tool.id"
        :title="tool.title"
        side="top"
        compact
        :side-offset-px="8"
      >
        <template #trigger>
          <button
            type="button"
            class="source-rte__tool"
            :class="{ 'is-on': tool.pressed }"
            :aria-label="tool.title"
            :aria-pressed="tool.pressed"
            :disabled="!canEdit"
            @click="tool.run"
          >
            <span class="source-rte__glyph" :style="maskStyle(tool.iconUrl)" />
          </button>
        </template>
      </SourceToolHoverCard>
    </span>

    <span class="source-rte__group" role="group" aria-label="Blocs">
      <SourceToolHoverCard
        v-for="tool in blockTools"
        :key="tool.id"
        :title="tool.title"
        side="top"
        compact
        :side-offset-px="8"
      >
        <template #trigger>
          <button
            type="button"
            class="source-rte__tool"
            :class="{ 'is-on': tool.pressed }"
            :aria-label="tool.title"
            :aria-pressed="tool.pressed"
            :disabled="!canEdit"
            @click="tool.run"
          >
            <span class="source-rte__glyph" :style="maskStyle(tool.iconUrl)" />
          </button>
        </template>
      </SourceToolHoverCard>
    </span>

    <span class="source-rte__group" role="group" aria-label="Lien">
      <SourceToolHoverCard
        v-for="tool in embedTools"
        :key="tool.id"
        :title="tool.title"
        side="top"
        compact
        :side-offset-px="8"
      >
        <template #trigger>
          <button
            type="button"
            class="source-rte__tool"
            :class="{ 'is-on': tool.pressed }"
            :aria-label="tool.title"
            :aria-pressed="tool.id === 'clean' ? undefined : tool.pressed"
            :disabled="!canEdit"
            :data-rte-link-tool="tool.id === 'link' ? '' : undefined"
            @click="tool.run"
          >
            <span class="source-rte__glyph" :style="maskStyle(tool.iconUrl)" />
          </button>
        </template>
      </SourceToolHoverCard>
    </span>
  </div>
</template>
